import React, { useState } from 'react';
import { supabase } from '../../services/supabaseClient';
import {
  BeakerIcon,
  ChartBarIcon,
  XMarkIcon,
  EyeIcon,
  MagnifyingGlassIcon
} from '@heroicons/react/24/outline';
import PatternFlowExamples from './PatternFlowExamples';

interface AnalysisRequest {
  selection_method: 'recent' | 'worst_scores' | 'best_scores' | 'random' | 'specific_grades' | 'specific_orgs';
  flow_limit: number;
  selection_filters?: {
    grades?: string[];
    orgIds?: string[];
    minScore?: number;
    maxScore?: number;
  };
}

interface Pattern {
  id: string;
  pattern_key: string;
  display_name: string;
  description: string;
  suggested_severity: string;
  suggested_penalty: number;
  flow_count: number;
  avg_confidence: number;
  dismissed: boolean;
  first_seen_at: string;
  last_seen_at: string;
}

interface AnalysisRun {
  id: string;
  selection_method: string;
  flows_analyzed: number;
  patterns_found: number;
  new_patterns_discovered: number;
  total_detections: number;
  status: string;
  created_at: string;
  completed_at: string;
}

export default function PatternDiscovery() {
  const [selectionMethod, setSelectionMethod] = useState<AnalysisRequest['selection_method']>('worst_scores');
  const [flowLimit, setFlowLimit] = useState(20);
  const [selectedGrades, setSelectedGrades] = useState<string[]>([]);
  const [analyzing, setAnalyzing] = useState(false);
  const [patterns, setPatterns] = useState<Pattern[]>([]);
  const [analysisRuns, setAnalysisRuns] = useState<AnalysisRun[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedPattern, setSelectedPattern] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'configure' | 'patterns' | 'history'>('configure');

  // Load patterns and history
  const loadData = async () => {
    setLoading(true);
    try {
      // Load patterns
      const { data: patternsData, error: patternsError } = await supabase
        .from('discovered_patterns')
        .select('*')
        .order('flow_count', { ascending: false });

      if (patternsError) throw patternsError;
      setPatterns(patternsData || []);

      // Load analysis runs
      const { data: runsData, error: runsError } = await supabase
        .from('pattern_analysis_runs')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(10);

      if (runsError) throw runsError;
      setAnalysisRuns(runsData || []);

    } catch (error) {
      console.error('Error loading data:', error);
      alert('Failed to load pattern data');
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    loadData();
  }, []);

  const startAnalysis = async () => {
    if (flowLimit < 1 || flowLimit > 100) {
      alert('Flow limit must be between 1 and 100');
      return;
    }

    setAnalyzing(true);

    try {
      const request: AnalysisRequest = {
        selection_method: selectionMethod,
        flow_limit: flowLimit
      };

      if (selectionMethod === 'specific_grades' && selectedGrades.length > 0) {
        request.selection_filters = { grades: selectedGrades };
      }

      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/analyze-flow-patterns`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`
          },
          body: JSON.stringify(request)
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Analysis failed');
      }

      const result = await response.json();

      alert(
        `Analysis complete!\n\n` +
        `Flows analyzed: ${result.flows_analyzed}\n` +
        `Patterns found: ${result.patterns_found}\n` +
        `New patterns: ${result.new_patterns_discovered}\n` +
        `Total detections: ${result.total_detections}`
      );

      // Reload data
      await loadData();
      setViewMode('patterns');

    } catch (error) {
      console.error('Analysis error:', error);
      alert(`Analysis failed: ${error.message}`);
    } finally {
      setAnalyzing(false);
    }
  };

  const dismissPattern = async (patternKey: string) => {
    try {
      const { error } = await supabase
        .from('discovered_patterns')
        .update({
          dismissed: true,
          dismissed_at: new Date().toISOString()
        })
        .eq('pattern_key', patternKey);

      if (error) throw error;

      // Reload patterns
      await loadData();
    } catch (error) {
      console.error('Error dismissing pattern:', error);
      alert('Failed to dismiss pattern');
    }
  };

  const estimatedCost = (flowLimit * 0.04).toFixed(2);

  const toggleGrade = (grade: string) => {
    setSelectedGrades(prev =>
      prev.includes(grade)
        ? prev.filter(g => g !== grade)
        : [...prev, grade]
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white shadow-sm rounded-lg p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <BeakerIcon className="h-8 w-8 text-purple-600" />
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Pattern Discovery (MVP)</h2>
              <p className="text-gray-600 mt-1">
                Discover new rubric items using AI analysis
              </p>
            </div>
          </div>
          <div className="flex space-x-2">
            <button
              onClick={() => setViewMode('configure')}
              className={`px-4 py-2 rounded-lg font-medium ${
                viewMode === 'configure'
                  ? 'bg-purple-100 text-purple-700'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Configure
            </button>
            <button
              onClick={() => setViewMode('patterns')}
              className={`px-4 py-2 rounded-lg font-medium ${
                viewMode === 'patterns'
                  ? 'bg-purple-100 text-purple-700'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Patterns ({patterns.filter(p => !p.dismissed).length})
            </button>
            <button
              onClick={() => setViewMode('history')}
              className={`px-4 py-2 rounded-lg font-medium ${
                viewMode === 'history'
                  ? 'bg-purple-100 text-purple-700'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              History
            </button>
          </div>
        </div>
      </div>

      {/* Configure View */}
      {viewMode === 'configure' && (
        <div className="bg-white shadow-sm rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Select Flows to Analyze
          </h3>

          {/* Selection Method */}
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Selection Method
              </label>
              <div className="space-y-2">
                {[
                  { value: 'recent', label: 'Most Recent', desc: 'Newest flows by last modified date' },
                  { value: 'worst_scores', label: 'Worst Scores', desc: 'Lowest scoring flows (most likely to have issues)' },
                  { value: 'best_scores', label: 'Best Scores', desc: 'Highest scoring flows (check for false negatives)' },
                  { value: 'random', label: 'Random Sample', desc: 'Representative baseline across all flows' },
                  { value: 'specific_grades', label: 'Specific Grades', desc: 'Only flows with selected grades' }
                ].map(option => (
                  <label key={option.value} className="flex items-start space-x-3 p-3 border rounded-lg hover:bg-gray-50 cursor-pointer">
                    <input
                      type="radio"
                      name="selection_method"
                      value={option.value}
                      checked={selectionMethod === option.value}
                      onChange={(e) => setSelectionMethod(e.target.value as any)}
                      className="mt-1"
                    />
                    <div className="flex-1">
                      <div className="font-medium text-gray-900">{option.label}</div>
                      <div className="text-sm text-gray-500">{option.desc}</div>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            {/* Grade Selection */}
            {selectionMethod === 'specific_grades' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Grades
                </label>
                <div className="flex space-x-2">
                  {['A', 'B', 'C', 'D', 'F'].map(grade => (
                    <button
                      key={grade}
                      onClick={() => toggleGrade(grade)}
                      className={`px-4 py-2 rounded-lg font-medium ${
                        selectedGrades.includes(grade)
                          ? 'bg-purple-600 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {grade}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Flow Limit */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Number of flows (max 100)
              </label>
              <input
                type="number"
                min="1"
                max="100"
                value={flowLimit}
                onChange={(e) => setFlowLimit(parseInt(e.target.value))}
                className="w-32 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>

            {/* Cost Estimate */}
            <div className="bg-yellow-50 p-4 rounded-lg">
              <div className="flex items-center space-x-2">
                <ChartBarIcon className="h-5 w-5 text-yellow-600" />
                <span className="font-medium text-yellow-900">
                  Estimated cost: ${estimatedCost} for {flowLimit} flows
                </span>
              </div>
            </div>

            {/* Start Button */}
            <button
              onClick={startAnalysis}
              disabled={analyzing}
              className="w-full bg-purple-600 hover:bg-purple-700 disabled:bg-gray-400 text-white font-semibold py-3 px-6 rounded-lg transition-colors"
            >
              {analyzing ? 'Analyzing...' : 'Start Analysis'}
            </button>
          </div>
        </div>
      )}

      {/* Patterns View */}
      {viewMode === 'patterns' && (
        <div className="space-y-4">
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-purple-500"></div>
            </div>
          ) : patterns.filter(p => !p.dismissed).length === 0 ? (
            <div className="bg-white shadow-sm rounded-lg p-12 text-center">
              <MagnifyingGlassIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No patterns discovered yet</h3>
              <p className="text-gray-500">Run an analysis to discover patterns</p>
            </div>
          ) : (
            patterns.filter(p => !p.dismissed).map(pattern => (
              <div key={pattern.id} className="bg-white shadow-sm rounded-lg p-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <h3 className="text-lg font-semibold text-gray-900">
                        {pattern.display_name}
                      </h3>
                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                        pattern.suggested_severity === 'critical' ? 'bg-red-100 text-red-800' :
                        pattern.suggested_severity === 'high' ? 'bg-orange-100 text-orange-800' :
                        pattern.suggested_severity === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-blue-100 text-blue-800'
                      }`}>
                        {pattern.suggested_severity}
                      </span>
                    </div>
                    <p className="text-gray-600 mb-3">{pattern.description}</p>
                    <div className="flex items-center space-x-6 text-sm text-gray-500">
                      <span>Found in <strong>{pattern.flow_count} flows</strong></span>
                      <span>Avg confidence: <strong>{(pattern.avg_confidence * 100).toFixed(0)}%</strong></span>
                      <span>Suggested: <strong>{pattern.suggested_penalty} points</strong></span>
                    </div>
                  </div>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => setSelectedPattern(pattern.pattern_key)}
                      className="px-3 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 font-medium text-sm flex items-center space-x-1"
                    >
                      <EyeIcon className="h-4 w-4" />
                      <span>View Examples</span>
                    </button>
                    <button
                      onClick={() => dismissPattern(pattern.pattern_key)}
                      className="px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 font-medium text-sm flex items-center space-x-1"
                    >
                      <XMarkIcon className="h-4 w-4" />
                      <span>Dismiss</span>
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* History View */}
      {viewMode === 'history' && (
        <div className="bg-white shadow-sm rounded-lg overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Method</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Flows</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Patterns</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">New</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Detections</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {analysisRuns.map(run => (
                <tr key={run.id}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {new Date(run.created_at).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                    {run.selection_method.replace('_', ' ')}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {run.flows_analyzed}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {run.patterns_found}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-green-600">
                    {run.new_patterns_discovered}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {run.total_detections}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 py-1 rounded text-xs font-medium ${
                      run.status === 'completed' ? 'bg-green-100 text-green-800' :
                      run.status === 'running' ? 'bg-blue-100 text-blue-800' :
                      run.status === 'failed' ? 'bg-red-100 text-red-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {run.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Flow Examples Modal */}
      {selectedPattern && (
        <PatternFlowExamples
          patternKey={selectedPattern}
          patternName={patterns.find(p => p.pattern_key === selectedPattern)?.display_name || selectedPattern}
          onClose={() => setSelectedPattern(null)}
        />
      )}
    </div>
  );
}
